export const GAME_ENTRANCE_PHASE_MS = 1000;
export const GAME_ENTRANCE_ROWS_START_MS = GAME_ENTRANCE_PHASE_MS / 2;
export const GAME_ENTRANCE_HEAD_START_MS = GAME_ENTRANCE_ROWS_START_MS + GAME_ENTRANCE_PHASE_MS;
export const GAME_ENTRANCE_DURATION_MS = GAME_ENTRANCE_HEAD_START_MS + GAME_ENTRANCE_PHASE_MS;

export function staggerDelay(index, count, phaseStart, itemDuration) {
  return phaseStart + (count > 1 ? index / (count - 1) : 0) * (GAME_ENTRANCE_PHASE_MS - itemDuration);
}

export function headEntranceTransform(source, target) {
  const x = source.left + source.width / 2 - target.left - target.width / 2;
  const y = source.top + source.height / 2 - target.top - target.height / 2;
  return `translate(${x}px, ${y}px) scale(${source.width / target.width}, ${source.height / target.height})`;
}

export function createGameEntrance({ game, track, board, head, onFinish,
  now = () => performance.now(), setTimer = setTimeout, clearTimer = clearTimeout,
  computedStyle = (node) => getComputedStyle(node) }) {
  let startedAt = null;
  let timer = null;
  let animations = [];

  function cancelAnimations() {
    animations.forEach((animation) => animation.cancel());
    animations = [];
  }

  function finish(notify = true) {
    const wasActive = startedAt !== null;
    startedAt = null;
    clearTimer(timer);
    timer = null;
    // These previews have already appeared; don't restart their normal CSS fade.
    if (wasActive) track.querySelectorAll(".is-preview").forEach((node) => { node.style.animation = "none"; });
    game.classList.remove("is-entering");
    cancelAnimations();
    if (wasActive && notify) onFinish();
  }

  function start() {
    finish(false);
    startedAt = now();
    game.classList.add("is-entering");
    timer = setTimer(() => finish(), GAME_ENTRANCE_DURATION_MS);
  }

  function render() {
    if (startedAt === null) return;
    const elapsed = now() - startedAt;
    if (elapsed >= GAME_ENTRANCE_DURATION_MS) return finish();
    cancelAnimations();
    const source = track.querySelector(".is-current");
    const sourceRect = source?.getBoundingClientRect();
    const targetRect = head.getBoundingClientRect();
    const sourceStyle = source && computedStyle(source);
    const targetStyle = computedStyle(head);
    const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
    function animate(node, frames, duration, delay) {
      const animation = node.animate(frames, { duration, delay, easing, fill: "both" });
      // A render or resize may replace nodes: resume the same entrance, never restart it.
      animation.currentTime = elapsed;
      animations.push(animation);
    }
    const letters = Array.from(track.children);
    letters.forEach((node, index) => animate(node, [
      { opacity: 0, transform: "translateY(-6px)" }, { opacity: 1, transform: "translateY(0)" }
    ], 180, staggerDelay(index, letters.length, 0, 180)));
    const rows = Array.from(board.querySelectorAll(".slot-row"));
    rows.forEach((node, index) => animate(node, [
      { opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "translateY(0)" }
    ], 240, staggerDelay(index, rows.length, GAME_ENTRANCE_ROWS_START_MS, 240)));
    if (sourceRect?.width && targetRect.width) {
      const origin = {
        transform: headEntranceTransform(sourceRect, targetRect),
        backgroundColor: sourceStyle.backgroundColor, color: sourceStyle.color,
        borderColor: sourceStyle.backgroundColor
      };
      animate(head, [
        { ...origin, opacity: 0, offset: 0 },
        { ...origin, opacity: 1, offset: 0.08 },
        { transform: "translate(0px, 0px) scale(1, 1)", opacity: 1,
          backgroundColor: targetStyle.backgroundColor, color: targetStyle.color,
          borderColor: targetStyle.borderColor, offset: 1 }
      ], GAME_ENTRANCE_PHASE_MS, GAME_ENTRANCE_HEAD_START_MS);
    }
  }

  return { start, render, finish, get active() { return startedAt !== null; } };
}
